import { computed, type ComputedRef, type Ref } from 'vue';
import type { Options, Point, TooltipFormatterContextObject } from 'highcharts';
import merge from 'lodash-es/merge';
import useColumnChartOptions from '@/components/charts/composables/useColumnChartOptions';
import { withAlpha } from '@/plugins/highcharts/chartColors';
import { i18n } from '@/plugins/internationalization/i18n';
import type { SlaughterRow } from '@/types';
import { toDateString, toUtcTimestamp, WEEK_MS } from './useRecordsAggregation';
import { CHART_HEIGHT, UNSELECTED_OPACITY, clearHoverBand, setHoverBand, weekTooltipHeader } from './useRecordsChartOptions';

/*
 * The Slaughters chart's own fork of `useRecordsAggregation`/`useRecordsChartOptions`, the same way
 * `useGroupedWeeklyChartOptions` forks them for a multi-series chart rather than generalising the
 * shared single-series ones. This one needs a second measure - discarded pigs, on its own y-axis -
 * that `RecordWeekBin` has no room for, and that measure is toggled rather than always drawn, which
 * the shared chart has no concept of either.
 */

export type SlaughterWeekBin = {
    weekStart: string;
    /** UTC midnight of `weekStart`, the x value Highcharts plots. */
    timestamp: number;
    pigs: number;
    discardedPigs: number;
};

/**
 * Pigs and discarded pigs per week, one point per week from the first populated week to the last -
 * mirrors `buildWeeklyBins`'s zero-filling so both series stay evenly spaced and every week stays
 * hoverable and clickable, but carries the second measure that shared function does not.
 */
export function buildSlaughterWeeklyBins(rows: SlaughterRow[]): SlaughterWeekBin[] {
    const byWeek = new Map<string, { pigs: number; discardedPigs: number }>();

    for (const row of rows) {
        const entry = byWeek.get(row.week_start) ?? { pigs: 0, discardedPigs: 0 };

        entry.pigs += row.number;
        // Not-yet-settled rows carry a null count - contribute nothing to the weekly total rather
        // than dropping the week or treating the whole bin as unknown.
        entry.discardedPigs += row.discarded_pigs ?? 0;
        byWeek.set(row.week_start, entry);
    }

    if (!byWeek.size) {
        return [];
    }

    const timestamps = [...byWeek.keys()].map(toUtcTimestamp);
    const first = Math.min(...timestamps);
    const last = Math.max(...timestamps);

    const bins: SlaughterWeekBin[] = [];

    for (let timestamp = first; timestamp <= last; timestamp += WEEK_MS) {
        const weekStart = toDateString(timestamp);
        const entry = byWeek.get(weekStart) ?? { pigs: 0, discardedPigs: 0 };

        bins.push({ weekStart, timestamp, pigs: entry.pigs, discardedPigs: entry.discardedPigs });
    }

    return bins;
}

export type SlaughtersChartConfig = {
    /** The main series' colour, from the new `chart.slaughters` token. */
    pigsColor: Ref<string>;
    /** The discarded series' colour, from `chart.slaughtersDiscarded`. */
    discardedColor: Ref<string>;
    title: Ref<string>;
    pigsSeriesName: Ref<string>;
    discardedSeriesName: Ref<string>;
    /** The chart draws the discarded series and its own y-axis only while this is on - default off. */
    showDiscarded: Ref<boolean>;
    onBinClicked: (weekStart: string) => void;
};

/**
 * The Slaughters weekly chart's Highcharts options: one grouped pair of columns per week, pig count
 * on the right axis always - the side every other records chart's single axis already reads from
 * (`useDefaultChartOptions`'s `yAxis.opposite: true`) - discarded count on a separate left-hand axis
 * only while the reader has switched it on. Either bar in a pair selects that week, same as every
 * other records chart.
 */
export function useSlaughtersChartOptions(bins: Ref<SlaughterWeekBin[]>, selectedWeekStart: Ref<string | null>, config: SlaughtersChartConfig): ComputedRef<Options> {
    // Static defaults only, taken once - see `useRecordsChartOptions` for why a `computed` cannot be
    // handed to the shared composable.
    const columnDefaults = useColumnChartOptions({});

    function pointColor(color: string, weekStart: string): string {
        return selectedWeekStart.value !== null && selectedWeekStart.value !== weekStart ? withAlpha(color, UNSELECTED_OPACITY) : color;
    }

    const pigsData = computed(() => bins.value.map((bin) => ({ x: bin.timestamp, y: bin.pigs, color: pointColor(config.pigsColor.value, bin.weekStart) })));
    const discardedData = computed(() => bins.value.map((bin) => ({ x: bin.timestamp, y: bin.discardedPigs, color: pointColor(config.discardedColor.value, bin.weekStart) })));

    // The app-wide axis styling every single-axis records chart gets from `columnDefaults.value.yAxis`
    // - same labels/title/gridline look, restated per entry below.
    const axisStyle = { labels: { style: { fontSize: '0.75rem' } }, title: { text: '' }, gridLineDashStyle: 'Dash' as const };

    return computed(() => {
        const base = merge({}, columnDefaults.value) as Options;

        // `columnDefaults.value.yAxis` is a single object; a lodash `merge` of the array below over
        // it would keep that object's own keys as index 0 rather than replacing it, the same trap
        // `PigTallyChart` avoids by deleting its own base yAxis first for the same reason.
        delete base.yAxis;

        return merge(base, {
            chart: {
                height: CHART_HEIGHT,
            },
            title: {
                text: config.title.value,
            },
            xAxis: {
                min: null,
                max: null,
            },
            // Array form, one entry per series - the dual-axis shape `PigTallyChart` uses for its
            // own two-scale chart. Pig count keeps the right axis every records chart already reads
            // from (`opposite: true`); the discarded axis sits on the left and only appears once the
            // reader turns that series on, so an empty scale is never shown for a series nobody asked
            // to see.
            yAxis: [
                { ...axisStyle, min: 0, allowDecimals: false, opposite: true },
                { ...axisStyle, min: 0, allowDecimals: false, gridLineWidth: 0, visible: config.showDiscarded.value },
            ],
            legend: {
                enabled: false,
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    borderWidth: 0,
                    borderRadius: 2,
                    pointPadding: 0.05,
                    groupPadding: 0.12,
                },
                series: {
                    // Highcharts dims every other series member while one is hovered, which fights
                    // the fade that marks a bar as unselected.
                    states: { inactive: { opacity: 1 } },
                    point: {
                        events: {
                            click() {
                                config.onBinClicked(toDateString((this as unknown as Point).x));
                            },
                            mouseOver() {
                                const point = this as unknown as Point;
                                const color = point.series?.index === 1 ? config.discardedColor.value : config.pigsColor.value;

                                setHoverBand(point.series?.chart, point.x, color);
                            },
                            mouseOut() {
                                clearHoverBand((this as unknown as Point).series?.chart);
                            },
                        },
                    },
                },
            },
            tooltip: {
                // One line per series still on the plot under a single week header - Highcharts
                // drops a hidden series from `this.points` on its own, so the discarded line simply
                // is not there while the checkbox has it switched off.
                shared: true,
                formatter(this: TooltipFormatterContextObject) {
                    const points = this.points ?? [];

                    if (!points.length) {
                        return '';
                    }

                    const lines = points.map((point) => `<span style="color:${point.color}">●</span> ${point.series.name}: <b>${i18n.global.n((point.y as number) ?? 0, 'decimal')}</b><br/>`).join('');

                    return weekTooltipHeader(points[0].x as number) + lines;
                },
            },
            series: [
                {
                    type: 'column',
                    name: config.pigsSeriesName.value,
                    yAxis: 0,
                    data: pigsData.value,
                },
                {
                    type: 'column',
                    name: config.discardedSeriesName.value,
                    yAxis: 1,
                    visible: config.showDiscarded.value,
                    data: discardedData.value,
                },
            ],
        });
    });
}
